// Client for Frankfurter (frankfurter.dev), a free exchange-rate API
// republishing the European Central Bank's daily reference rates. No API
// key required, unlike e.g. Twelve Data (used by the community tool
// github.com/rbjansen/dual-actual for the same purpose).
//
// The `fetchImpl` parameter exists purely so this is unit-testable without
// hitting the real network -- defaults to the global fetch.

const BASE_URL = 'https://api.frankfurter.dev/v1';

// Frankfurter only covers a subset of Actual's full currency list --
// notably not AED. Verified directly against GET /v1/currencies; there's no
// indication this set changes often, so it's hardcoded rather than fetched
// at render/request time. Shared between the desktop UI's automatic-fetch
// currency picker (which shouldn't offer an unsupported currency at all)
// and the server-side auto-fetch handler (which needs to skip an
// unsupported pair rather than let it abort every other pair in the same
// call, since it has no UI to filter what it's given).
export const FRANKFURTER_SUPPORTED_CURRENCIES = new Set([
  'AUD',
  'BRL',
  'CAD',
  'CHF',
  'CNY',
  'CZK',
  'DKK',
  'EUR',
  'GBP',
  'HKD',
  'HUF',
  'IDR',
  'ILS',
  'INR',
  'ISK',
  'JPY',
  'KRW',
  'MXN',
  'MYR',
  'NOK',
  'NZD',
  'PHP',
  'PLN',
  'RON',
  'SEK',
  'SGD',
  'THB',
  'TRY',
  'USD',
  'ZAR',
]);

export type FetchedRate = { date: string; rate: number };

/**
 * Fetches daily exchange rates for a currency pair over a date range
 * (inclusive) in a single request. The ECB doesn't publish rates on
 * weekends/bank holidays, so the result may have gaps -- callers relying on
 * getRateForDate's "most recent rate on or before" lookup handle this
 * correctly already, so gaps are not backfilled here.
 *
 * Throws if the request fails (network error, unsupported currency code,
 * etc.) -- callers decide how to handle that, this function doesn't swallow
 * errors or return partial/default data.
 */
export async function fetchRatesFromFrankfurter(
  fromCurrency: string,
  toCurrency: string,
  startDate: string,
  endDate: string,
  fetchImpl: typeof fetch = fetch,
): Promise<FetchedRate[]> {
  // Frankfurter's officially documented v1 query params are base/symbols
  // (from/to also work as aliases -- verified directly against the live
  // API, both forms return identical results with base correctly set to
  // fromCurrency -- but using the documented names avoids relying on
  // undocumented aliases that could change).
  const url =
    `${BASE_URL}/${encodeURIComponent(startDate)}..${encodeURIComponent(endDate)}` +
    `?base=${encodeURIComponent(fromCurrency)}&symbols=${encodeURIComponent(toCurrency)}`;

  const res = await fetchImpl(url);
  if (!res.ok) {
    throw new Error(
      `Frankfurter request failed (${res.status} ${res.statusText}) for ${fromCurrency} -> ${toCurrency}`,
    );
  }

  const data = (await res.json()) as {
    rates: Record<string, Record<string, number>>;
  };

  return Object.entries(data.rates)
    .map(([date, ratesForDate]) => ({ date, rate: ratesForDate[toCurrency] }))
    .filter((entry): entry is FetchedRate => entry.rate != null)
    .sort((a, b) => (a.date < b.date ? -1 : 1));
}
